import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from './icons';
import { useHaptic } from '../../hooks/useHaptic';
import {
  accrualProgress,
  daysRunning,
  lockDaysLeft,
  lockProgress,
  msToAccrual,
  periodEarning,
  unrealizedProfit,
  type StakePosition,
} from '../../lib/staking';
import {
  PROFIT_GREEN,
  fitFontSize,
  fmtDate,
  fmtDateTime,
  fmtUsd,
  fmtWhole,
  tint,
  type PositionMeta,
} from './stakeMeta';

type Props = {
  position: StakePosition;
  meta: PositionMeta;
  /** Admin cadence; default 24. Drives the next-accrual progress bar. */
  accrualEveryHours?: number;
  onWithdraw: (p: StakePosition) => void;
  onLocked: (p: StakePosition) => void;
  onShare: (p: StakePosition) => void;
  onTopUp: (p: StakePosition) => void;
};

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * Accrual follows the admin period (default 24h). Minutes under an hour,
 * hours under two days (so "12 hours" is not rounded up to "1 day").
 */
function useNextPayoutLabel(p: StakePosition) {
  const { t } = useTranslation();
  const ms = msToAccrual(p);
  if (ms <= 0) return t('stake.accruingNow');
  if (ms < HOUR_MS) {
    return t('stake.nextInMinutes', { count: Math.max(1, Math.ceil(ms / MINUTE_MS)) });
  }
  const hours = Math.max(1, Math.round(ms / HOUR_MS));
  if (hours < 48) return t('stake.nextInHours', { count: hours });
  return t('stake.nextInDays', { count: Math.max(1, Math.ceil(ms / DAY_MS)) });
}

function Actions({
  p,
  meta,
  onTopUp,
  onWithdraw,
  onLocked,
}: {
  p: StakePosition;
  meta: PositionMeta;
  onTopUp: (p: StakePosition) => void;
  onWithdraw: (p: StakePosition) => void;
  onLocked: (p: StakePosition) => void;
}) {
  const { t } = useTranslation();
  const { impact } = useHaptic();
  // Maximum: lock sheet while the term runs. Monthly: profit withdraw is open.
  const lockEnds =
    p.lockEndsAt ??
    (meta.withdrawMode !== 'full'
      ? new Date(new Date(p.openedAt).getTime() + meta.termDays * DAY_MS).toISOString()
      : null);
  const showLock =
    meta.withdrawMode === 'lock' && !!lockEnds && new Date(lockEnds).getTime() > Date.now();
  return (
    <div className="flex gap-2 pt-3.5">
      {meta.topUpAllowed && (
        <button
          type="button"
          onClick={() => onTopUp(p)}
          className="h-11 flex-1 rounded-[13px] bg-black/[0.05] text-[14px] font-semibold text-ink active:bg-black/[0.1]"
        >
          {t('stake.topUp')}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          impact('light');
          if (showLock) onLocked(p);
          else onWithdraw(p);
        }}
        className="h-11 flex-1 rounded-[13px] text-[14px] font-semibold active:opacity-80"
        style={{ background: tint(meta.accent, 0.13), color: meta.accent }}
      >
        {meta.withdrawMode === 'profit' ? t('stake.withdrawProfit') : t('stake.withdraw')}
      </button>
    </div>
  );
}

function Divider() {
  return <div className="my-4 h-px bg-black/[0.07]" />;
}

/** Current-line vault card — matches the INCPT HTML prototype. */
function VaultPositionCard({
  position: p,
  meta,
  accrualEveryHours = 24,
  onWithdraw,
  onLocked,
  onShare,
  onTopUp,
}: Props) {
  const { t, i18n } = useTranslation();
  const { impact, selection } = useHaptic();
  const [open, setOpen] = useState(false);

  const profit = unrealizedProfit(p);
  const profitPct = p.current > 0 ? Math.max(2, Math.min(98, (profit / p.current) * 100)) : 0;
  // Liquid accrues/pays daily; Monthly + Maximum use the monthly earn figure
  // and show the principal lock-up bar (HTML vaultCard).
  const liquid = meta.withdrawMode === 'full';
  const nextLabel = useNextPayoutLabel(p);
  const days = daysRunning(p);
  const periodEarn = periodEarning(p, meta.dailyRate, liquid ? 'daily' : 'monthly');
  // Prefer the API date; fall back so the lock bar never renders empty.
  const lockEndsAt =
    p.lockEndsAt ??
    (liquid
      ? null
      : new Date(new Date(p.openedAt).getTime() + meta.termDays * DAY_MS).toISOString());
  const lockPos = lockEndsAt ? { ...p, lockEndsAt } : p;
  const lockLeft = lockDaysLeft(lockPos);
  const lockPct = Math.round(lockProgress(lockPos) * 100);
  const limitPct =
    meta.maxAmount > 0 ? Math.min(100, Math.round((p.invested / meta.maxAmount) * 100)) : 0;
  const lockDate = lockEndsAt ? fmtDate(lockEndsAt, i18n.language) : '';
  const nextPct = Math.round(accrualProgress(p, 'daily', Date.now(), accrualEveryHours) * 100);

  return (
    <div className="rounded-[20px] bg-white px-[21px] pb-[17px] pt-[19px] shadow-card">
      <button
        type="button"
        onClick={() => {
          selection();
          setOpen((v) => !v);
        }}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between gap-2.5">
          <div>
            <div className="text-[16px] font-medium tracking-[-0.2px] text-ink">
              {t('stake.earnedIn', { count: days })}
            </div>
            <div className="mt-[3px] text-[12px] text-muted">
              {meta.name} · #{p.id}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <span
              className="inline-block rounded-full px-[11px] py-1 text-[13px] font-semibold"
              style={{ color: meta.accent, background: tint(meta.accent, 0.12) }}
            >
              {meta.rate}
            </span>
            <div className="mt-1.5 text-[12px] text-muted">
              {t('stake.inStaking', { amount: fmtWhole(p.current) })}
            </div>
          </div>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            impact('light');
            onShare(p);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              onShare(p);
            }
          }}
          className="mt-1.5 inline-block max-w-full cursor-pointer whitespace-nowrap font-normal leading-[1.05] tracking-[-2px]"
          style={{ color: meta.accent, fontSize: fitFontSize(`+${fmtUsd(p.profit)}`, 42, 11) }}
        >
          +{fmtUsd(p.profit)}
        </div>

        <div className="mt-2 flex items-center justify-between gap-2.5">
          <span className="text-[13px] text-muted">
            {liquid ? t('stake.forecastToday') : t('stake.forecastMonth')}{' '}
            <span className="font-medium text-ink">+{fmtUsd(periodEarn)}</span>
          </span>
          <ChevronRight
            size={18}
            className={`shrink-0 text-[#c0c0cc] transition-transform duration-300 ${
              open ? '-rotate-90' : 'rotate-90'
            }`}
          />
        </div>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <Divider />
          <div className="mb-2.5 text-[14px] font-medium text-ink">{t('stake.depositSum')}</div>
          <div className="mb-2.5 flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1 text-[13px] text-muted">
            <span className="flex items-center">
              <i
                className="mr-1.5 inline-block h-[7px] w-[7px] rounded-[2px]"
                style={{ background: meta.accent }}
              />
              <span>
                {t('stake.deposited')}{' '}
                <b className="font-semibold text-ink">{fmtWhole(p.invested)}</b>
              </span>
            </span>
            <span className="flex items-center">
              <i
                className="mr-1.5 inline-block h-[7px] w-[7px] rounded-[2px]"
                style={{ background: PROFIT_GREEN }}
              />
              <span>
                {t('stake.profitLabel')}{' '}
                <b className="font-semibold" style={{ color: PROFIT_GREEN }}>
                  +{fmtUsd(p.profit)}
                </b>
              </span>
            </span>
          </div>
          <div className="flex h-1.5 gap-[2px]">
            <i
              className="block h-full rounded-l"
              style={{ background: meta.accent, width: `${100 - profitPct}%` }}
            />
            <i
              className="block h-full rounded-r"
              style={{ background: PROFIT_GREEN, width: `${profitPct}%` }}
            />
          </div>

          <Divider />
          <div className="mb-2.5 text-[14px] font-medium text-ink">
            {t('stake.nextAccrualTitle')}
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-black/[0.06]">
            <i
              className="block h-full rounded transition-[width] duration-500"
              style={{
                background: meta.accent,
                width: `${nextPct}%`,
              }}
            />
          </div>
          <div className="mt-2.5 text-[13px] text-muted">{nextLabel}</div>

          <Divider />
          {liquid ? (
            <>
              <div className="mb-2.5 text-[14px] font-medium text-ink">
                {t('stake.vaultLimit', { vault: meta.name })}
              </div>
              <div className="h-1.5 overflow-hidden rounded bg-black/[0.06]">
                <i
                  className="block h-full rounded"
                  style={{ background: meta.accent, width: `${limitPct}%` }}
                />
              </div>
              <div className="mt-2.5 text-[13px] text-muted">
                {t('stake.stakedOf', {
                  amount: fmtWhole(p.invested),
                  max: fmtWhole(meta.maxAmount),
                })}
              </div>
            </>
          ) : (
            <>
              <div className="mb-2.5 text-[14px] font-medium text-ink">
                {t('stake.principalLock')}
              </div>
              <div className="h-1.5 overflow-hidden rounded bg-black/[0.06]">
                <i
                  className="block h-full rounded"
                  style={{ background: meta.accent, width: `${lockPct}%` }}
                />
              </div>
              <div className="mt-2.5 text-[13px] text-muted">
                {t('stake.lockUntil', {
                  date: lockDate,
                  left: t('stake.lockLeft', { count: lockLeft }),
                })}
              </div>
            </>
          )}

          <div className="mb-1.5 mt-4 h-px bg-black/[0.07]" />
          <Row label={t('stake.opened')} value={fmtDateTime(p.openedAt, i18n.language)} />
          <Row label={t('stake.withdrawnRow')} value={fmtWhole(p.withdrawn)} />
          {liquid ? (
            <Row label={t('stake.withdraw')} value={t('stake.withdrawAnytime')} />
          ) : (
            <>
              <Row
                label={t('stake.withdrawProfit')}
                value={
                  meta.withdrawMode === 'profit'
                    ? t('stake.profitOutMonthly', {
                        date: p.nextAccrualAt ? fmtDate(p.nextAccrualAt, i18n.language) : '—',
                      })
                    : t('stake.profitOutTerm', { date: lockDate || '—' })
                }
              />
              <Row
                label={t('stake.principalLabel')}
                value={t('stake.principalOut', { date: lockDate || '—' })}
              />
            </>
          )}

          <Actions
            p={p}
            meta={meta}
            onTopUp={onTopUp}
            onWithdraw={onWithdraw}
            onLocked={onLocked}
          />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-[5px] text-[13px]">
      <span className="text-muted">{label}</span>
      <span className="text-right font-semibold text-ink">{value}</span>
    </div>
  );
}

/** Discontinued-line card — compact header from the prototype. */
function LegacyPositionCard({ position: p, meta, onWithdraw, onLocked, onShare, onTopUp }: Props) {
  const { t, i18n } = useTranslation();
  const { impact, selection } = useHaptic();
  const [open, setOpen] = useState(false);

  const profit = unrealizedProfit(p);
  const profitPct = p.current > 0 ? Math.max(2, Math.min(98, (profit / p.current) * 100)) : 0;
  const nextLabel = useNextPayoutLabel(p);

  const rows: [string, string][] = [
    [t('stake.opened'), fmtDateTime(p.openedAt, i18n.language)],
    [t('stake.planType'), `${meta.name} (${t('stake.oldBadge')})`],
    [t('stake.rate'), meta.rate],
    [t('stake.profitLabel'), meta.payout],
    [t('stake.withdrawnRow'), fmtWhole(p.withdrawn)],
    p.lockEndsAt
      ? [
          t('stake.lockEnds'),
          `${fmtDate(p.lockEndsAt, i18n.language)} · ${t('stake.months', { count: 12 })}`,
        ]
      : [t('stake.principalLabel'), meta.principal],
  ];

  return (
    <div className="rounded-[20px] bg-white p-3.5 shadow-card">
      <button
        type="button"
        onClick={() => {
          selection();
          setOpen((v) => !v);
        }}
        className="w-full text-left"
      >
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
            style={{ background: meta.accent }}
          />
          <span className="text-[14px] font-medium text-ink">{meta.short}</span>
          <span className="text-[11.5px] text-muted">#{p.id}</span>
          <span className="flex-1" />
          <span className="whitespace-nowrap text-[16px] font-medium text-ink">
            {fmtWhole(p.current)}
          </span>
          <ChevronRight
            size={15}
            className={`shrink-0 text-black/25 transition-transform duration-300 ${
              open ? '-rotate-90' : 'rotate-90'
            }`}
          />
        </div>

        <div className="mt-2 flex items-center gap-3 text-[12px] text-muted">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="h-[7px] w-[7px] rounded-[2px]" style={{ background: meta.accent }} />
            <span>
              {t('stake.deposited')} <b className="font-medium text-ink">{fmtWhole(p.invested)}</b>
            </span>
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              impact('light');
              onShare(p);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onShare(p);
              }
            }}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap"
          >
            <i className="h-[7px] w-[7px] rounded-[2px]" style={{ background: PROFIT_GREEN }} />
            <span>
              {t('stake.profitLabel')}{' '}
              <b className="font-medium" style={{ color: PROFIT_GREEN }}>
                +{fmtUsd(p.profit)}
              </b>
            </span>
          </span>
          <span className="flex-1" />
          <span
            className="whitespace-nowrap rounded-lg px-2 py-[3px] text-[10.5px] font-semibold"
            style={{ color: meta.accent, background: tint(meta.accent, 0.14) }}
          >
            {meta.rate}
          </span>
        </div>

        <div className="mt-2.5 flex h-[5px] gap-[2px]">
          <i
            className="block h-full rounded-l-[3px]"
            style={{ background: meta.accent, width: `${100 - profitPct}%` }}
          />
          <i
            className="block h-full rounded-r-[3px]"
            style={{ background: PROFIT_GREEN, width: `${profitPct}%` }}
          />
        </div>

        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[11.5px] text-muted">{nextLabel}</span>
          <span className="flex-1" />
          <span className="h-[3px] w-16 overflow-hidden rounded-full bg-black/[0.06]">
            <i
              className="block h-full rounded-full transition-[width] duration-500"
              style={{
                background: tint(meta.accent, 0.75),
                width: `${accrualProgress(p, meta.accrual) * 100}%`,
              }}
            />
          </span>
        </div>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-3 border-t border-black/[0.06] pt-2">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 py-[5px] text-[12.5px]">
                <span className="text-muted">{label}</span>
                <span className="text-right font-medium text-ink">{value}</span>
              </div>
            ))}
            <Actions
              p={p}
              meta={meta}
              onTopUp={onTopUp}
              onWithdraw={onWithdraw}
              onLocked={onLocked}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** One deposit: current vaults use the earn-first card; the OLD line stays compact. */
export function StakePositionCard(props: Props) {
  return props.meta.legacy ? <LegacyPositionCard {...props} /> : <VaultPositionCard {...props} />;
}
